import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Popper,
  ClickAwayListener
} from '@mui/material';
import type { Category } from '../types';
import { categoryService } from '../services/bookService';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';

interface CategoryFormProps {
  open: boolean;
  onClose: () => void;
  onSave: (category: Category) => void;
  onError?: (error: string) => void;
  category?: Category | null;
  title: string;
}

export const CategoryForm: React.FC<CategoryFormProps> = ({ open, onClose, onSave, onError, category, title }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingCategories, setExistingCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { snackbar, showWarning, hideSnackbar } = useSnackbar();
  const firstInputRef = useRef<HTMLInputElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      if (category) {
        setFormData({
          name: category.name,
          description: category.description || ''
        });
      } else {
        setFormData({
          name: '',
          description: ''
        });
      }
      setError(null);
      setDuplicateWarning(null);
      
      // Load existing categories for suggestions (both create and edit)
      loadExistingCategories();
      
      // Focus first input
      setTimeout(() => {
        firstInputRef.current?.focus();
      }, 100);
    }
  }, [open, category]);

  const loadExistingCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await categoryService.getCategories({ page: 1, pageSize: 1000, type: 'book' });
      setExistingCategories(response.categories || []);
    } catch (err) {
      console.error('Error loading existing categories:', err);
    } finally {
      setLoadingCategories(false);
    }
  };

  const normalizeText = (text: string): string => {
    return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };

  const checkDuplicateCategory = (name: string): Category | null => {
    if (!name.trim()) return null;
    const normalizedName = normalizeText(name);
    return existingCategories.find(c => normalizeText(c.name) === normalizedName) || null;
  };

  // Filter suggestions based on input (show similar items)
  const filteredSuggestions = useMemo(() => {
    if (!formData.name.trim() || formData.name.length < 1) return [];
    const normalizedInput = normalizeText(formData.name);
    return existingCategories.filter(c => 
      normalizeText(c.name).includes(normalizedInput) || 
      c.name.toLowerCase().includes(formData.name.toLowerCase())
    ).slice(0, 10); // Limit to 10 suggestions
  }, [formData.name, existingCategories]);

  const handleInputChange = (field: 'name' | 'description') => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
    
    // Check for duplicate in real-time (only when creating new and typing name)
    if (field === 'name' && !category && value.trim()) {
      const duplicate = checkDuplicateCategory(value);
      if (duplicate) {
        setDuplicateWarning(`Danh mục "${duplicate.name}" đã tồn tại.`);
      } else {
        setDuplicateWarning(null);
      }
    } else if (field === 'name') {
      setDuplicateWarning(null);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Tên danh mục là bắt buộc');
      return;
    }

    // Check for duplicate (case-insensitive) when creating new
    if (!category) {
      const duplicate = checkDuplicateCategory(formData.name.trim());
      if (duplicate) {
        showWarning(`Danh mục "${formData.name.trim()}" đã tồn tại.`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    setDuplicateWarning(null);

    try {
      let savedCategory: Category;
      
      if (category) {
        // Update existing category
        savedCategory = await categoryService.updateCategory(category.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined
        });
      } else {
        // Create new category (with type='book')
        savedCategory = await categoryService.createCategory({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          type: 'book'
        });
      }
      
      onSave(savedCategory);
      onClose();
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || err?.error || 'Có lỗi xảy ra khi lưu danh mục';
      // Check if it's a duplicate error
      if (errorMessage.includes('đã tồn tại') || errorMessage.includes('already exists')) {
        showWarning(`Danh mục "${formData.name.trim()}" đã tồn tại.`);
      } else {
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
            <Box ref={inputContainerRef} sx={{ position: 'relative' }}>
              <TextField
                inputRef={firstInputRef}
                fullWidth
                label="Tên danh mục *"
                value={formData.name}
                onChange={handleInputChange('name')}
                onFocus={() => setShowSuggestions(true)}
                required
                disabled={loading}
                error={!!error && !formData.name.trim() || !!duplicateWarning}
                helperText={
                  error && !formData.name.trim() 
                    ? error 
                    : duplicateWarning
                      ? duplicateWarning
                      : !category
                        ? 'Nhập tên danh mục. Hệ thống sẽ gợi ý các danh mục đã có để tránh trùng lặp.'
                        : 'Nhập tên danh mục'
                }
              />
              {/* Suggestions dropdown */}
              <Popper
                open={showSuggestions && filteredSuggestions.length > 0 && !loading}
                anchorEl={inputContainerRef.current}
                placement="bottom-start"
                style={{ zIndex: 1301, width: inputContainerRef.current?.offsetWidth }}
              >
                <Paper elevation={3} sx={{ maxHeight: 200, overflow: 'auto', mt: 0.5 }}>
                  <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', bgcolor: 'grey.100', fontWeight: 600 }}>
                    Danh mục đã tồn tại (tham khảo):
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {filteredSuggestions.map((cat) => (
                      <ListItem 
                        key={cat.id}
                        sx={{ 
                          bgcolor: normalizeText(cat.name) === normalizeText(formData.name) ? 'warning.light' : 'transparent',
                          '&:hover': { bgcolor: 'grey.100' }
                        }}
                      >
                        <ListItemText 
                          primary={cat.name}
                          secondary={normalizeText(cat.name) === normalizeText(formData.name) ? '⚠️ Trùng tên!' : cat.description}
                          primaryTypographyProps={{ 
                            fontWeight: normalizeText(cat.name) === normalizeText(formData.name) ? 600 : 400 
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Popper>
            </Box>
          </ClickAwayListener>
          <TextField
            fullWidth
            label="Mô tả"
            value={formData.description}
            onChange={handleInputChange('description')}
            multiline
            rows={4}
            disabled={loading}
            placeholder="Nhập mô tả cho danh mục (tùy chọn)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !formData.name.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Đang lưu...' : 'Lưu'}
        </Button>
      </DialogActions>
      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Dialog>
  );
};
